<script setup lang="ts">
import { computed, type PropType } from 'vue';
import GaiaDangerConfirmModal from '@/components/design/composite/GaiaDangerConfirmModal.vue';
import GaiaText from '@/components/design/base/GaiaText.vue';

/**
 * Confirms cutting a user out of an organisation — the user leaving on their
 * own (`leave`, typed confirmation is the organisation's name) or an admin
 * removing them (`remove`, typed confirmation is the user's email). One
 * component for both so the two dialogs say the same thing about what the cut
 * does: every tie is severed for good, while what the user uploaded stays with
 * the organisation, visible to its administrators only.
 */
const props = defineProps({
  show: {
    type: Boolean,
    required: true,
  },
  mode: {
    type: String as PropType<'leave' | 'remove'>,
    required: true,
  },
  organisationName: {
    type: String,
    required: true,
  },
  /** The user being removed — required in `remove` mode. */
  userEmail: {
    type: String,
    default: '',
  },
  loading: {
    type: Boolean,
    default: false,
  },
  error: {
    type: String as PropType<string | null>,
    default: null,
  },
});

const emit = defineEmits<{ 'update:show': [value: boolean]; confirm: [] }>();

const isLeave = computed(() => props.mode === 'leave');
const title = computed(() =>
  isLeave.value ? `Leave ${props.organisationName}` : `Remove ${props.userEmail}`
);
const confirmValue = computed(() => (isLeave.value ? props.organisationName : props.userEmail));
const confirmLabel = computed(() => (isLeave.value ? 'Leave organisation' : 'Remove user'));
</script>

<template>
  <gaia-danger-confirm-modal
    :show="props.show"
    :title="title"
    :confirm-value="confirmValue"
    :confirm-label="confirmLabel"
    :loading="props.loading"
    :error="props.error"
    @update:show="value => emit('update:show', value)"
    @confirm="emit('confirm')"
  >
    <template v-if="isLeave">
      Leaving severs every tie between you and
      <gaia-text weight="semibold">{{ props.organisationName }}</gaia-text
      >. Your ownerships are unassigned and your roles removed. This cannot be undone. Everything
      you uploaded stays in the organisation and remains visible to its administrators only.
    </template>
    <template v-else>
      Removing <gaia-text weight="semibold">{{ props.userEmail }}</gaia-text> severs every tie
      between them and <gaia-text weight="semibold">{{ props.organisationName }}</gaia-text
      >. Their ownerships are unassigned and their roles removed. This cannot be undone. Everything
      they uploaded stays in the organisation and remains visible to its administrators only.
    </template>
  </gaia-danger-confirm-modal>
</template>
